import { Component, ElementRef, EventEmitter, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CalendarDate } from './calendar-date.interface';
import moment, { Moment } from 'moment';

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS }
  ],
  encapsulation: ViewEncapsulation.None
})
export class DatePickerComponent {
  public isDatePickerOpened: boolean = false;
  public dates: CalendarDate[];
  public startDate: Moment;
  public endDate: Moment;
  public formattedStartDate: string;
  public formattedEndDate: string;
  public today = moment(new Date());
  public minimumDate = moment(1970).startOf('year');
  public activeDate: CalendarDate;
  private _tempStartDate: Moment;
  private _tempEndDate: Moment;
  private _tempActiveDate: CalendarDate;
  private _displayedCustomStartDate: string;

  @ViewChild('startCalendar')
  public startCalendar: ElementRef;

  @ViewChild('endCalendar')
  public endCalendar: ElementRef;

  @Output()
  public dateChanged: EventEmitter<object> = new EventEmitter();

  constructor() {
    this.dates = [
      {name: 'datePicker.today', startDate: moment().startOf('day') , endDate: moment().endOf('day')},
      {name: 'datePicker.yesterday', startDate: moment().subtract(1, 'd').startOf('day'), endDate: moment().subtract(1, 'd').endOf('day')},
      {name: 'datePicker.thisWeek', startDate: moment().startOf('isoWeek'), endDate: moment().endOf('day')},
      {name: 'datePicker.lastWeek', startDate: moment().subtract(1, 'weeks').startOf('isoWeek'), endDate: moment().subtract(1, 'weeks').endOf('isoWeek')},
      {name: 'datePicker.thisMonth', startDate: moment().startOf('month'), endDate: moment().endOf('day')},
      {name: 'datePicker.lastSevenDays', startDate: moment().subtract(7, 'd').startOf('day'), endDate: moment().endOf('day')},
      {name: 'datePicker.lastThirtyDays', startDate: moment().subtract(30, 'd').startOf('day'), endDate: moment().endOf('day')},
      {name: 'datePicker.custom'}
    ];
  }

  setInterval(selectedDate): void {
    this.activeDate = selectedDate;
    if (selectedDate.name === 'datePicker.custom' && this.isDatePickerOpened) {
      this.startCalendar.nativeElement.focus();
    } else {
      this.startDate = this.activeDate.startDate;
      this.endDate = this.activeDate.endDate;
      this.dateChanged.emit({start: this.startDate, end: this.endDate});
      this._closeDatePicker();
    }
    this.setDisplayedDates();
  }

  setDisplayedDates(): void {
    this.formattedStartDate = moment(this.startDate).format('MM/DD/YYYY');
    this.formattedEndDate = moment(this.endDate).format('MM/DD/YYYY');
  }

  isDateActive(date): boolean {
    return this.activeDate === date;
  }

  toggleDatePicker(event: Event): void {
    // NOTE: This stopPropagation is required, because the bubbling causes the datepicker to close
    event.stopPropagation();
    this.isDatePickerOpened = !this.isDatePickerOpened;

    if (this.isDatePickerOpened) {
      this._tempStartDate = this.startDate;
      this._tempEndDate = this.endDate;
      this._tempActiveDate = this.activeDate;
    }
  }

  setNewStartDateValue(newDate): void {
    this.startCalendar.nativeElement.focus();
    this._selectCustomRangeOption();
    const endOfNewDate = this._getEndOfNewDate(newDate);
    if (moment(this.endDate).isBefore(newDate)) {
      this.startDate = newDate;
      this.endDate = endOfNewDate;
    } else {
      this.startDate = newDate;
    }
    this.setDisplayedDates();
  }

  setNewEndDateValue(newDate): void {
    this.endCalendar.nativeElement.focus();
    this._selectCustomRangeOption();
    const endOfNewDate = this._getEndOfNewDate(newDate);
    if (moment(newDate).isBefore(this.startDate)) {
      this.startDate = newDate;
      this.endDate = endOfNewDate;
    } else {
      this.endDate = endOfNewDate;
    }
    this.setDisplayedDates();
  }

  onCancel(): void {
    this.startDate = this._tempStartDate;
    this.endDate = this._tempEndDate;
    this.activeDate = this._tempActiveDate;
    this._closeDatePicker();
  }

  onApply(): void {
    this.dateChanged.emit({start: this.startDate, end: this.endDate});
    this._closeDatePicker();
  }

  isCustomDateSelected(): boolean {
    if (this.activeDate) {
      return this.activeDate.name === 'datePicker.custom';
    }
  }

  getActiveDateName(): string {
    if (this.activeDate) {
      return this.activeDate.name;
    }
  }

  createDisplayedCustomDates(): string {
    if (moment(this.startDate).isSame(this.endDate, 'year')) {
      this._displayedCustomStartDate = moment(this.startDate).format('MMM D');
    } else {
      this._displayedCustomStartDate = moment(this.startDate).format('MMM D, YYYY');
    }
    const displayedCustomEndDate = moment(this.endDate).format('MMM D, YYYY');
    return this._displayedCustomStartDate + '-' + displayedCustomEndDate;
  }

  private _closeDatePicker(): void {
    this.isDatePickerOpened = false;
  }

  private _getEndOfNewDate(newDate): Moment {
    return moment(newDate).endOf('day');
  }

  private _selectCustomRangeOption(): void {
    this.activeDate = this.dates[this.dates.length - 1];
  }
}
